import { useSuspenseQuery } from '@apollo/client';
import React from 'react';

import { Card, Grid, Heading, Stack, Text } from '@eluve/components';
import { Location } from '@eluve/frontend-feature-location-hierarchy';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';

const getSyncedLocationStatsQuery = graphql(`
  query getSyncedLocationStats($tenantId: uuid!, $locationId: String!) {
    locationsByPk(tenantId: $tenantId, id: $locationId) {
      __typename
      id
      appointmentsAggregate {
        aggregate {
          count
        }
      }
      location_users {
        __typename
        locationId
        userId
      }
    }
  }
`);

const SourceField: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <Stack gap={0} align="start">
    <Text size="xs" color="secondary">
      {label}
    </Text>
    {children}
  </Stack>
);

export const SyncedLocationStats: React.FC<{
  tenantId: string;
  locationId: string;
}> = ({ tenantId, locationId }) => {
  const { t } = useTranslation();
  const { data } = useSuspenseQuery(getSyncedLocationStatsQuery, {
    variables: { tenantId, locationId },
  });

  const userCount = data.locationsByPk?.location_users.length ?? 0;
  const appointmentCount =
    data.locationsByPk?.appointmentsAggregate.aggregate?.count ?? 0;

  return (
    <Grid columns={1} gap={5} className="md:grid-cols-2">
      <Card className="w-full rounded-xl border-border-primary p-6">
        <Stack gap={1} align="start">
          <Text size="sm" color="secondary">
            {t('tenantAdmin.common.users')}
          </Text>
          <Heading size="3xl" weight="semibold" level={3}>
            {userCount}
          </Heading>
          <Text size="xs" color="secondary">
            {t('tenantAdmin.location.usersStatDescription')}
          </Text>
        </Stack>
      </Card>
      <Card className="w-full rounded-xl border-border-primary p-6">
        <Stack gap={1} align="start">
          <Text size="sm" color="secondary">
            {t('tenantAdmin.location.appointmentsStatTitle')}
          </Text>
          <Heading size="3xl" weight="semibold" level={3}>
            {appointmentCount.toLocaleString()}
          </Heading>
          <Text size="xs" color="secondary">
            {t('tenantAdmin.location.appointmentsStatDescription')}
          </Text>
        </Stack>
      </Card>
    </Grid>
  );
};

/** Read-only sync provenance grid for EMR-synced locations. */
export const SyncedSourceGrid: React.FC<{
  location: Location;
}> = ({ location }) => {
  const { t } = useTranslation();

  return (
    <div className="grid max-w-3xl grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
      <SourceField label={t('tenantAdmin.location.sourceOrgIdLabel')}>
        <Stack gap={0} align="start">
          <Text className="font-mono font-medium">{location.externalId}</Text>
          <Text size="xs" color="secondary">
            {t('tenantAdmin.location.sourceOrgIdHint')}
          </Text>
        </Stack>
      </SourceField>
      <SourceField label={t('tenantAdmin.location.sourceNameLabel')}>
        <Text>{location.sourceName ?? '—'}</Text>
      </SourceField>
    </div>
  );
};
